import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MoreHorizontal, KeyRound, Pencil, Trash2, Ban, CheckCircle, Lock, Copy, RefreshCw, ShieldCheck, PowerOff, Power } from "lucide-react";
import { regenerateBypassCode } from "@/lib/mfa.functions";
import { AppHeader } from "@/components/app/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAllProfiles, useAllRoles, type AdminProfile } from "@/hooks/use-admin-data";
import { useAuth, DEPARTMENT_LABELS, type Department, type AppRole } from "@/lib/auth-context";
import { DEPARTMENT_CONFIGS } from "@/lib/department-configs";
import {
  WORKSPACE_CAP_LABELS,
  WORKSPACE_CAP_DESCRIPTIONS,
  SECTION_KEYS,
  type SectionKey,
  type ModeratorPermissions,
} from "@/lib/moderator-permissions";
import { Switch } from "@/components/ui/switch";
import {
  createUser,
  deactivateUser,
  reactivateUser,
  resetUserPassword,
  updateModeratorPermissions,
  updateUser,
} from "@/lib/rpc/admin.functions";
import {
  checkDeleteEmployeeEligibility,
  initiateDeleteEmployee,
  confirmDeleteEmployee,
} from "@/lib/rpc/delete-employee.functions";
import { setKanbanPermissions } from "@/lib/kanban.functions";

const QUICK_PERMISSIONS = ["task_assign", "creatives_review", "send_notifications"] as const;
type QuickPermission = (typeof QUICK_PERMISSIONS)[number];

const DEPTS = Object.keys(DEPARTMENT_CONFIGS) as Department[];
const ROLES: AppRole[] = ["admin", "moderator", "user"];

export const Route = createFileRoute("/_app/_admin/admin/users")({
  component: () => <Navigate to="/admin/settings" search={{ tab: "users" } as never} />,
});

export function UsersPage() {
  const { role, user } = useAuth();
  const isAdmin = role === "admin";
  const currentUserId = user?.id ?? null;
  const { data: profiles = [] } = useAllProfiles();
  const { data: roles = [] } = useAllRoles();
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AdminProfile | null>(null);
  const [resetting, setResetting] = useState<AdminProfile | null>(null);
  const [deactivating, setDeactivating] = useState<AdminProfile | null>(null);
  const [deleting, setDeleting] = useState<AdminProfile | null>(null);

  const rolesByUser = useMemo(() => {
    const m = new Map<string, AppRole>();
    for (const r of roles) {
      const existing = m.get(r.user_id);
      const rank = (x: AppRole) => (x === "admin" ? 3 : x === "moderator" ? 2 : 1);
      if (!existing || rank(r.role) > rank(existing)) m.set(r.user_id, r.role);
    }
    return m;
  }, [roles]);

  const filtered = useMemo(() => {
    const list = profiles.filter((p) => {
      if (deptFilter !== "all" && p.department !== deptFilter) return false;
      const isInactive = p.status !== "active";
      if (statusFilter === "active" && isInactive) return false;
      if (statusFilter === "inactive" && !isInactive) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        return (
          p.full_name.toLowerCase().includes(q) ||
          p.email.toLowerCase().includes(q)
        );
      }
      return true;
    });
    // Default sort: active first, then alphabetical by name
    return [...list].sort((a, b) => {
      const aActive = a.status === "active" ? 0 : 1;
      const bActive = b.status === "active" ? 0 : 1;
      if (aActive !== bActive) return aActive - bActive;
      return (a.full_name || a.email || "").localeCompare(b.full_name || b.email || "");
    });
  }, [profiles, search, deptFilter, statusFilter]);

  return (
    <>
      <AppHeader
        subtitle="Create, edit and manage all users"
        ctaLabel={isAdmin ? "+ Add User" : undefined}
        onCta={() => setCreateOpen(true)}
      />
      <div className="px-[22px] pb-6">
        <div className="rounded-lg border border-border bg-card shadow-sm">
          <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
                Roster
              </div>
              <h2 className="text-[16px] font-bold">{profiles.length} users</h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or email…"
                className="h-9 w-56 text-[12px]"
              />
              <Select value={deptFilter} onValueChange={setDeptFilter}>
                <SelectTrigger className="h-9 w-44 text-[12px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All departments</SelectItem>
                  {DEPTS.map((d) => (
                    <SelectItem key={d} value={d}>
                      {DEPARTMENT_LABELS[d]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={statusFilter}
                onValueChange={(v) => setStatusFilter(v as "all" | "active" | "inactive")}
              >
                <SelectTrigger className="h-9 w-32 text-[12px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="h-32 text-center text-[12px] text-muted-foreground">
                    No users match.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((p) => {
                const r = rolesByUser.get(p.id) ?? "user";
                return (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.full_name}</TableCell>
                    <TableCell className="text-[12px] text-muted-foreground">{p.email}</TableCell>
                    <TableCell className="text-[12px]">{DEPARTMENT_LABELS[p.department]}</TableCell>
                    <TableCell className="text-[12px] capitalize">{r}</TableCell>
                    <TableCell className="text-[12px]">{p.department === "editor" ? p.monthly_target : "—"}</TableCell>
                    <TableCell>
                      {(() => {
                        const isActive = p.status === "active";
                        return (
                          <span
                            className={`inline-flex h-6 items-center rounded-full px-2 text-[10px] font-medium ${
                              isActive
                                ? "bg-emerald-100 text-emerald-800"
                                : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {isActive ? "Active" : "Inactive"}
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {isAdmin && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEditing(p)}>
                              <Pencil className="mr-2 h-3.5 w-3.5" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setResetting(p)}>
                              <KeyRound className="mr-2 h-3.5 w-3.5" /> Reset password
                            </DropdownMenuItem>
                            <DeactivateOrReactivateItem
                              profile={p}
                              currentUserId={currentUserId}
                              onRequestDeactivate={() => setDeactivating(p)}
                            />
                            {p.id !== currentUserId && (
                              <DropdownMenuItem
                                onClick={() => setDeleting(p)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete permanently…
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      {createOpen && (
        <CreateUserDialog open={createOpen} onOpenChange={setCreateOpen} />
      )}
      {editing && (
        <EditUserDialog
          open
          onOpenChange={(o) => !o && setEditing(null)}
          profile={editing}
          currentRole={rolesByUser.get(editing.id) ?? "user"}
        />
      )}
      {resetting && (
        <ResetPasswordDialog
          open
          onOpenChange={(o) => !o && setResetting(null)}
          profile={resetting}
        />
      )}
      <AlertDialog open={!!deactivating} onOpenChange={(o) => !o && setDeactivating(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate {deactivating?.full_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This user will lose dashboard access but their past submissions, creatives,
              tasks, payroll, leaves, and logs will remain safely stored.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!deactivating) return;
                try {
                  await deactivateUser({ data: { user_id: deactivating.id } });
                  toast.success("User deactivated");
                  setDeactivating(null);
                } catch (e) {
                  toast.error((e as Error).message);
                }
              }}
            >
              Deactivate User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {deleting && (
        <DeleteEmployeeDialog
          profile={deleting}
          onClose={() => setDeleting(null)}
        />
      )}
    </>
  );
}

function DeactivateOrReactivateItem({
  profile,
  currentUserId,
  onRequestDeactivate,
}: {
  profile: AdminProfile;
  currentUserId: string | null;
  onRequestDeactivate: () => void;
}) {
  const qc = useQueryClient();
  const isActive = profile.status === "active";
  if (isActive) {
    return (
      <DropdownMenuItem
        className="text-destructive"
        onClick={() => {
          if (currentUserId && profile.id === currentUserId) {
            toast.error("You cannot deactivate your own account.");
            return;
          }
          onRequestDeactivate();
        }}
      >
        <PowerOff className="mr-2 h-3.5 w-3.5" /> Deactivate User
      </DropdownMenuItem>
    );
  }
  return (
    <DropdownMenuItem
      onClick={async () => {
        try {
          await reactivateUser({ data: { user_id: profile.id } });
          qc.invalidateQueries({ queryKey: ["admin"] });
          toast.success("User reactivated");
        } catch (e) {
          toast.error((e as Error).message);
        }
      }}
    >
      <Power className="mr-2 h-3.5 w-3.5" /> Reactivate User
    </DropdownMenuItem>
  );
}

const createSchema = z.object({
  email: z.string().email(),
  password: z.string().min(6, "At least 6 chars"),
  full_name: z.string().min(1),
  employee_id: z.string().regex(/^\d{4,}$/, "Numeric, at least 4 digits"),
  department: z.enum(DEPTS as [Department, ...Department[]]),
  role: z.enum(["admin", "moderator", "user"]),
  monthly_target: z.coerce.number().int().min(0).max(1000),
});
type CreateValues = z.infer<typeof createSchema>;

function CreateUserDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const form = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      email: "",
      password: "",
      full_name: "",
      employee_id: "",
      department: "editor",
      role: "user",
      // Default 80 only matches the editor department (the form's initial department).
      monthly_target: 80,
    },
  });

  const mut = useMutation({
    mutationFn: (v: CreateValues) => createUser({ data: v }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin"] });
      toast.success("User created");
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl">
        <DialogHeader>
          <DialogTitle>Add a user</DialogTitle>
          <DialogDescription>They'll be able to sign in immediately.</DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit((v) => mut.mutate(v))} className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Full name" error={form.formState.errors.full_name?.message}>
              <Input {...form.register("full_name")} />
            </FieldRow>
            <FieldRow label="Email" error={form.formState.errors.email?.message}>
              <Input type="email" {...form.register("email")} />
            </FieldRow>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Employee ID *" error={form.formState.errors.employee_id?.message}>
              <Input inputMode="numeric" placeholder="e.g. 1001" {...form.register("employee_id")} />
            </FieldRow>
            <FieldRow label="Temporary password" error={form.formState.errors.password?.message}>
              <Input type="text" {...form.register("password")} placeholder="Share with the user" />
            </FieldRow>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <FieldRow label="Department">
              <Select
                value={form.watch("department")}
                onValueChange={(v) => form.setValue("department", v as Department)}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DEPTS.map((d) => (
                    <SelectItem key={d} value={d}>{DEPARTMENT_LABELS[d]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldRow>
            <FieldRow label="Role">
              <Select
                value={form.watch("role")}
                onValueChange={(v) => form.setValue("role", v as AppRole)}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldRow>
            <FieldRow label="Monthly target">
              <Input type="number" min={0} {...form.register("monthly_target")} />
            </FieldRow>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={mut.isPending}>
              {mut.isPending ? "Creating…" : "Create user"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const editSchema = z.object({
  department: z.enum(DEPTS as [Department, ...Department[]]),
  role: z.enum(["admin", "moderator", "user"]),
  monthly_target: z.coerce.number().int().min(0).max(1000).nullish(),
  can_add_vault_entries: z.boolean(),
});
type EditValues = z.infer<typeof editSchema>;

function EditUserDialog({
  open,
  onOpenChange,
  profile,
  currentRole,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  profile: AdminProfile;
  currentRole: AppRole;
}) {
  const qc = useQueryClient();
  const form = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      department: profile.department,
      role: currentRole,
      monthly_target: profile.monthly_target,
      can_add_vault_entries:
        profile.can_add_vault_entries ??
        (currentRole === "admin" || currentRole === "moderator"),
    },
  });
  const watchedRole = form.watch("role");
  const watchedVault = form.watch("can_add_vault_entries");

  const initialPerms = (profile.moderator_permissions ?? {}) as ModeratorPermissions;
  const [perms, setPerms] = useState<Record<QuickPermission, boolean>>({
    task_assign: Boolean(initialPerms.task_assign),
    creatives_review: Boolean(initialPerms.creatives_review),
    send_notifications: Boolean(initialPerms.send_notifications),
  });

  // Section-visibility toggles (default: ON when key absent — backward compat)
  const [sections, setSections] = useState<Record<SectionKey, boolean>>(() => {
    const out = {} as Record<SectionKey, boolean>;
    for (const s of SECTION_KEYS) {
      const v = initialPerms[s.key];
      out[s.key] = v === undefined ? true : Boolean(v);
    }
    return out;
  });
  const [scopeOwnDept, setScopeOwnDept] = useState<boolean>(
    Boolean(initialPerms.scope_own_department),
  );

  const mut = useMutation({
    mutationFn: async (v: EditValues) => {
      await updateUser({ data: { user_id: profile.id, ...v } });
      if (v.role === "moderator") {
        const merged: ModeratorPermissions = {
          ...initialPerms,
          task_assign: perms.task_assign,
          creatives_review: perms.creatives_review,
          send_notifications: perms.send_notifications,
          scope_own_department: scopeOwnDept,
          ...sections,
        };
        await updateModeratorPermissions({
          data: {
            user_id: profile.id,
            permissions: merged as unknown as Record<string, unknown>,
          },
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin"] });
      toast.success("Updated");
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] flex flex-col rounded-xl">
        <DialogHeader>
          <DialogTitle>Edit {profile.full_name}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto min-h-0">
        <form id="edit-user-form" onSubmit={form.handleSubmit((v) => mut.mutate(v))} className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <LockedField label="Employee ID" value={profile.employee_id ?? "—"} />
            <LockedField label="Full name" value={profile.full_name} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <FieldRow label="Department">
              <Select
                value={form.watch("department")}
                onValueChange={(v) => form.setValue("department", v as Department)}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DEPTS.map((d) => (
                    <SelectItem key={d} value={d}>{DEPARTMENT_LABELS[d]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldRow>
            <FieldRow label="Role">
              <Select
                value={form.watch("role")}
                onValueChange={(v) => form.setValue("role", v as AppRole)}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldRow>
            {form.watch("department") === "editor" && (
              <FieldRow label="Monthly target">
                <Input type="number" min={0} {...form.register("monthly_target")} />
              </FieldRow>
            )}
          </div>

          {watchedRole === "moderator" && (
            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
                Permissions
              </div>
              <div className="flex flex-col gap-2">
                {QUICK_PERMISSIONS.map((cap) => (
                  <label
                    key={cap}
                    className="flex items-start justify-between gap-3 rounded-md border border-border bg-background p-2"
                  >
                    <div className="min-w-0">
                      <div className="text-[12px] font-medium">{WORKSPACE_CAP_LABELS[cap]}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {WORKSPACE_CAP_DESCRIPTIONS[cap]}
                      </div>
                    </div>
                    <Switch
                      checked={perms[cap]}
                      onCheckedChange={(v) => setPerms((p) => ({ ...p, [cap]: v }))}
                    />
                  </label>
                ))}
              </div>
              <p className="mt-2 text-[10px] text-muted-foreground">
                For full per-department permissions, use the Moderators tab.
              </p>
            </div>
          )}

          {watchedRole === "moderator" && (
            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
                Section Access
              </div>
              <p className="mb-2 text-[10px] text-muted-foreground">
                Toggle which sidebar sections this moderator can see. OFF hides the menu item
                and blocks the route (redirects to Dashboard). Visibility only — data access rules are unchanged.
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {SECTION_KEYS.map((s) => (
                  <label
                    key={s.key}
                    className="flex items-center justify-between gap-3 rounded-md border border-border bg-background px-2 py-1.5"
                  >
                    <span className="text-[12px] font-medium">{s.label}</span>
                    <Switch
                      checked={sections[s.key]}
                      onCheckedChange={(v) => setSections((p) => ({ ...p, [s.key]: v }))}
                    />
                  </label>
                ))}
              </div>

              <div className="mt-3 rounded-md border border-border bg-background p-2">
                <label className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[12px] font-medium">Team views: Own department only</div>
                    <div className="text-[10px] text-muted-foreground">
                      When ON, Today's Work / By Department / By Employee show only this
                      moderator's own department. The department picker is locked.
                    </div>
                  </div>
                  <Switch checked={scopeOwnDept} onCheckedChange={setScopeOwnDept} />
                </label>
              </div>
            </div>
          )}

          <div className="rounded-lg border border-border bg-muted/30 p-3">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
              Vault Permissions
            </div>
            <label className="flex items-start justify-between gap-3 rounded-md border border-border bg-background p-2">
              <div className="min-w-0">
                <div className="text-[12px] font-medium">Can Add Vault Entries</div>
                <div className="text-[10px] text-muted-foreground">
                  If enabled, this user can create new Vault entries. If disabled, they can only view entries shared with them.
                </div>
              </div>
              <Switch
                checked={watchedVault}
                onCheckedChange={(v) => form.setValue("can_add_vault_entries", v)}
              />
            </label>
          </div>

          <KanbanPermissionsPanel profile={profile} />

          <BypassCodePanel profile={profile} />

        </form>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit" form="edit-user-form" disabled={mut.isPending}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({
  open,
  onOpenChange,
  profile,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  profile: AdminProfile;
}) {
  const [pwd, setPwd] = useState("");
  const mut = useMutation({
    mutationFn: () =>
      resetUserPassword({ data: { user_id: profile.id, password: pwd } }),
    onSuccess: () => {
      toast.success("Password updated");
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl">
        <DialogHeader>
          <DialogTitle>Reset password</DialogTitle>
          <DialogDescription>
            Set a new temporary password for {profile.full_name}.
          </DialogDescription>
        </DialogHeader>
        <FieldRow label="New password">
          <Input value={pwd} onChange={(e) => setPwd(e.target.value)} />
        </FieldRow>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => mut.mutate()} disabled={pwd.length < 6 || mut.isPending}>
            Update
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-1.5">
      <Label className="text-[11px] font-medium">{label}</Label>
      {children}
      {error && <p className="text-[11px] text-destructive">{error}</p>}
    </div>
  );
}

function LockedField({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-1.5">
      <Label className="flex items-center gap-1 text-[11px] font-medium">
        {label}
        <Lock
          className="h-3 w-3 text-muted-foreground"
          aria-label="This field cannot be changed once set. Contact system administrator if correction is needed."
        >
          <title>This field cannot be changed once set. Contact system administrator if correction is needed.</title>
        </Lock>
      </Label>
      <div className="flex h-9 items-center rounded-md border border-input bg-muted/40 px-3 text-[12px] text-muted-foreground">
        {value || "—"}
      </div>
    </div>
  );
}

function BypassCodePanel({ profile }: { profile: AdminProfile }) {
  const qc = useQueryClient();
  const [code, setCode] = useState<string>("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let cancelled = false;
    supabase
      .from("profiles_private")
      .select("mfa_bypass_code")
      .eq("id", profile.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setCode((data?.mfa_bypass_code as string) ?? "");
      });
    return () => { cancelled = true; };
  }, [profile.id]);
  const copy = () => {
    navigator.clipboard.writeText(code);
    toast.success("Code copied");
  };
  const regen = async () => {
    if (!confirm("Generate a new 6-digit bypass code? The old one will stop working.")) return;
    setBusy(true);
    try {
      const r = await regenerateBypassCode({ data: { user_id: profile.id } });
      setCode(r.code);
      qc.invalidateQueries({ queryKey: ["admin"] });
      toast.success("New code generated");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-lg border border-purple-700/40 bg-gradient-to-br from-[#1a0b2e] to-[#0b0514] p-4 shadow-sm">
      <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.4px] text-purple-200">
        <ShieldCheck className="h-3.5 w-3.5 text-purple-300" />
        MFA bypass code
        <span className="ml-auto rounded-full bg-purple-500/20 px-2 py-0.5 text-[10px] font-medium text-purple-100 ring-1 ring-purple-400/40">
          MFA: {profile.mfa_enabled ? "enabled" : "disabled"}
        </span>
      </div>
      <p className="text-[10px] leading-relaxed text-purple-200/80">
        Permanent 6-digit code that bypasses MFA at login. Visible to admins only — share with the
        user as their backup code.
      </p>
      <div className="mt-2 flex items-center gap-2">
        <code className="flex-1 rounded-md border border-purple-500/30 bg-black px-3 py-2 text-center font-mono text-lg tracking-[0.3em] text-purple-100">
          {code || "------"}
        </code>
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={copy}
          disabled={!code}
          className="border-purple-500/40 bg-purple-500/10 text-purple-100 hover:bg-purple-500/20 hover:text-white"
        >
          <Copy className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={regen}
          disabled={busy}
          className="border-purple-500/40 bg-purple-500/10 text-purple-100 hover:bg-purple-500/20 hover:text-white"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
        </Button>
      </div>
    </div>
  );
}

function KanbanPermissionsPanel({ profile }: { profile: AdminProfile }) {
  const qc = useQueryClient();
  const initial = profile as never as { is_team_lead?: boolean };
  const [leader, setLeader] = useState<boolean>(Boolean(initial.is_team_lead));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await setKanbanPermissions({
        data: { user_id: profile.id, is_team_lead: leader },
      });
      qc.invalidateQueries({ queryKey: ["admin"] });
      toast.success("Task management role updated");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
        Task Management Role
      </div>
      <div className="grid gap-2">
        <label className="flex items-start justify-between gap-3 rounded-md border border-border bg-background p-2">
          <div className="min-w-0">
            <div className="text-[12px] font-medium">Team Lead</div>
            <div className="text-[10px] text-muted-foreground">
              Team leads can create department boards and manage their own department's board (assign work, manage members, plan the timeline).
            </div>
          </div>
          <Switch checked={leader} onCheckedChange={setLeader} />
        </label>
        <div className="flex justify-end">
          <Button type="button" size="sm" onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function DeleteEmployeeDialog({
  profile,
  onClose,
}: {
  profile: AdminProfile;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [phase, setPhase] = useState<"checking" | "blocked" | "confirm" | "sending" | "otp" | "deleting">("checking");
  const [hasData, setHasData] = useState<boolean | null>(null);
  const [otpId, setOtpId] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [maskedTo, setMaskedTo] = useState<string>("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { hasData } = await checkDeleteEmployeeEligibility({
          data: { user_id: profile.id },
        });
        if (!alive) return;
        setHasData(hasData);
        setPhase(hasData ? "blocked" : "confirm");
      } catch (e) {
        if (!alive) return;
        toast.error((e as Error).message);
        onClose();
      }
    })();
    return () => {
      alive = false;
    };
  }, [profile.id, onClose]);

  const sendOtp = async () => {
    setPhase("sending");
    try {
      const res = await initiateDeleteEmployee({ data: { user_id: profile.id } });
      setOtpId(res.otp_id);
      setMaskedTo(res.sent_to_masked);
      setPhase("otp");
      toast.success(`Verification code sent to ${res.sent_to_masked}`);
    } catch (e) {
      toast.error((e as Error).message);
      setPhase("confirm");
    }
  };

  const confirmDelete = async () => {
    if (!otpId) return;
    setPhase("deleting");
    try {
      await confirmDeleteEmployee({
        data: { user_id: profile.id, otp_id: otpId, otp_code: otpCode.trim() },
      });
      toast.success(`${profile.full_name} was permanently deleted`);
      await qc.invalidateQueries();
      onClose();
    } catch (e) {
      toast.error((e as Error).message);
      setPhase("otp");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete {profile.full_name}?</DialogTitle>
          <DialogDescription>
            This is irreversible. Only accounts with zero linked data may be
            hard-deleted.
          </DialogDescription>
        </DialogHeader>

        {phase === "checking" && (
          <p className="text-sm text-muted-foreground">Checking for linked data…</p>
        )}

        {phase === "blocked" && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            This employee has linked data (submissions, tasks, chat, HR, etc.).
            Permanent deletion is blocked — <strong>deactivate instead</strong> so
            historical records stay intact.
          </div>
        )}

        {phase === "confirm" && hasData === false && (
          <div className="space-y-2 text-sm">
            <p>
              No linked data found. A 6-digit verification code will be sent to
              the security email address on file. You'll need to enter it to
              complete deletion.
            </p>
          </div>
        )}

        {phase === "sending" && (
          <p className="text-sm text-muted-foreground">Sending verification code…</p>
        )}

        {(phase === "otp" || phase === "deleting") && (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Enter the 6-digit code sent to <strong>{maskedTo}</strong>. Code
              expires in 10 minutes.
            </p>
            <Input
              autoFocus
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              placeholder="000000"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              disabled={phase === "deleting"}
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={phase === "sending" || phase === "deleting"}>
            {phase === "blocked" ? "Close" : "Cancel"}
          </Button>
          {phase === "confirm" && (
            <Button variant="destructive" onClick={sendOtp}>
              Send verification code
            </Button>
          )}
          {phase === "otp" && (
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={otpCode.length !== 6}
            >
              Delete permanently
            </Button>
          )}
          {phase === "deleting" && (
            <Button variant="destructive" disabled>
              Deleting…
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


